import { makeStyles, MenuItem, Theme } from "@material-ui/core";
import { FilterList } from "@material-ui/icons";
import React from "react";

import {
  Filter,
  FilterMenu,
  FilterRadioButton,
  FilterSearch,
} from "@ofqual/portal-components/components/ResponseFilter";

import { useLookup, useSearch, useUrl } from "@ofqual/portal-core/core/hooks";
import { Select } from "@ofqual/portal-components/components/Select";
import { newsTypeService, tagsService } from "../../services";
import { toGroupedList } from "@ofqual/portal-core/core/utils/listHelpers";
import { ItemType } from "@ofqual/portal-components/components/types";
import { ensureParamIsArray } from "@ofqual/portal-core/core/utils/urlHelper";

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    menuItem: {
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
  };
});

export const FilterControls: React.FC = () => {
  const classes = useStyles();

  const {
    searchParams,
    navigateToNextSearch,
    searchParamsFiltersOnly,
  } = useSearch(useUrl());

  const [tagsLookups, newsTypeLookups] = useLookup(
    {
      service: tagsService,
      id: "id",
      label: "tag",
    },
    newsTypeService
  );

  const groupedTagsLookup = toGroupedList(tagsLookups, {
    id: "id",
    label: "tag",
    group: "tagCategory",
  });
  return (
    <>
      <FilterSearch
        title="Search..."
        value={searchParams.search}
        onChange={(value) =>
          navigateToNextSearch({
            search: value,
          })
        }
        dataTest={"search"}
      />
      &nbsp;&nbsp;
      <Filter
        Icon={FilterList}
        title="Order By"
        badge={!!searchParams.order}
        dataTest="news-list-order"
      >
        <FilterRadioButton
          isChecked={typeof searchParams.order !== "string"}
          value={undefined}
          text={"Created date"}
          dataTest={"sort-created-date"}
          onChange={() =>
            navigateToNextSearch({
              order: undefined,
              orderDirection: undefined,
            })
          }
        />
        <FilterRadioButton
          isChecked={searchParams.order === "UpdatedDate"}
          value={"UpdatedDate"}
          text={"Updated date"}
          dataTest="sort-updated"
          onChange={() =>
            navigateToNextSearch({
              order: "UpdatedDate",
              orderDirection: "desc",
            })
          }
        />
      </Filter>
      <FilterMenu
        Icon={FilterList}
        title="Filters"
        dataTest="filter"
        search={{ searchParamsFiltersOnly, navigateToNextSearch }}
      >
        {(workingParams, dirtyParams, addParam) => (
          <form>
            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("type") ? classes.root : undefined
                }
                label="Type"
                data-test="type-filter"
                value={ensureParamIsArray(workingParams.type)}
                onChange={(e) => addParam("type", e.target.value)}
                selectVariant="multiple"
              >
                {newsTypeLookups.map((x) => ({
                  ...x,
                  id: x.id.toString(),
                }))}
              </Select>
            </MenuItem>
            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("tags") ? classes.root : undefined
                }
                label="Tag(s)"
                data-test="tags-filter"
                value={ensureParamIsArray(workingParams.tags)}
                onChange={(e) => addParam("tags", e.target.value)}
                selectVariant="multiple"
              >
                {
                  groupedTagsLookup.map((x) => ({
                    ...x,
                    id: x.id ? x.id.toString() : "",
                  })) as ItemType[]
                }
              </Select>
            </MenuItem>
            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("archived") ? classes.root : undefined
                }
                label="Status"
                data-test="status-filter"
                value={workingParams.archived}
                onChange={(e) => addParam("archived", e.target.value)}
              >
                {[
                  { id: false, label: "Active" },
                  { id: true, label: "Archived" },
                ]}
              </Select>
            </MenuItem>
            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("published") ? classes.root : undefined
                }
                label="Published"
                data-test="published-filter"
                value={workingParams.published}
                onChange={(e) => addParam("published", e.target.value)}
              >
                {[
                  { id: true, label: "Published" },
                  { id: false, label: "Unpublished" },
                ]}
              </Select>
            </MenuItem>
          </form>
        )}
      </FilterMenu>
    </>
  );
};
